import { useMemo } from 'react';
import { VideoCarouselProps } from './contracts';
import VideoCarouselUI from './VideoCarouselUI';
import { usePublishedVideosQuery, useInfinitePublishedVideosQuery } from '@data/videos/usePublishedVideosQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader/SkeletonLoader';
import { PaginatedResponse, Video } from '@repo/types';

export function VideoCarousel({
  tags,
  tagsMatchMode,
  excludeTags,
  videoChannels,
  sortOrder,
  limit = 6,
  lazyLoad = false,
  ...props
}: VideoCarouselProps) {
  const queryArgs = {
    tags: tags ?? undefined,
    tagsMatchMode: tagsMatchMode ?? undefined,
    excludeTags: excludeTags ?? undefined,
    videoChannelIds: videoChannels ?? undefined,
    sortOrder: sortOrder ?? undefined,
    limit: limit ?? undefined,
  };

  // Use regular query when lazyLoad is disabled
  const regularQuery = usePublishedVideosQuery({ ...queryArgs, page: 1 }, { enabled: !lazyLoad });

  // Use infinite query when lazyLoad is enabled
  const infiniteQuery = useInfinitePublishedVideosQuery(queryArgs, { enabled: lazyLoad });

  const isLoading = lazyLoad ? infiniteQuery.isLoading : regularQuery.isLoading;
  const error = lazyLoad ? infiniteQuery.error : regularQuery.error;

  // Flatten all videos from infinite query pages
  const allVideos = useMemo(() => {
    if (!lazyLoad || !infiniteQuery.data) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.items);
  }, [lazyLoad, infiniteQuery.data]);

  // Create a synthetic PaginatedResponse for the UI component
  const paginatedVideos: PaginatedResponse<Video> | undefined = useMemo(() => {
    if (lazyLoad && infiniteQuery.data?.pages && infiniteQuery.data.pages.length > 0) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      return {
        items: allVideos,
        pagination: lastPage.pagination,
      };
    }
    return regularQuery.data;
  }, [lazyLoad, infiniteQuery.data, allVideos, regularQuery.data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={3} />
      </div>
    );
  }

  if (!paginatedVideos || error) {
    return null;
  }

  return (
    <VideoCarouselUI
      paginatedVideos={paginatedVideos}
      allVideos={allVideos}
      lazyLoad={lazyLoad}
      hasNextPage={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      fetchNextPage={infiniteQuery.fetchNextPage}
      {...props}
    />
  );
}
